import React, { useContext, useEffect } from "react";
import { gql } from "@urql/core";
import { SurveyContext, QuestionProps } from "../survey";
import { CounterpartsQuestionDocument } from "../../generated/graphql";
import QuestionLabel from "./components/question_label";
import Input from "../../components/data_entry/input";
import styled from "styled-components";
import { useQuery } from "urql";
import { captureError } from "../../../utils/capture_error";

gql`
  query CounterpartsQuestion($participantId: ID!) {
    participant(id: $participantId) {
      id
      __typename
      mentorships(status: MATCHED) {
        id
        student {
          __typename
          id
          user {
            id
            firstName
            lastName
          }
        }
        mentor {
          __typename
          id
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

interface Answer {
  [mentorshipId: string]: string;
}

interface Customizations {
  prompt: string;
  inputType: "text" | "number";
  description?: string;
  optional?: boolean;
  placeholder?: string;
}

const Question = styled.div`
  .counterpart {
    margin-top: ${({ theme }) => theme.deprecatedSpacing.component.three};
  }
`;

export default function CounterpartsInfo({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
}: QuestionProps<Answer, Customizations>) {
  const { participantId } = useContext(SurveyContext);

  const [{ fetching, error, data }] = useQuery({
    query: CounterpartsQuestionDocument,
    variables: { participantId },
  });

  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const description = customizations.description;
  const placeholder = customizations.placeholder;
  const filledOut =
    !!data &&
    !!currentAnswer &&
    data.participant.mentorships.every((mentorship) =>
      Object.keys(currentAnswer).includes(mentorship.id),
    );
  let answerIsValid = optional ? true : typeof currentAnswer !== "undefined" && filledOut;
  answerIsValid =
    // if the input is a number, make sure none of the answers are less than 0
    customizations.inputType === "number" &&
    currentAnswer &&
    Object.keys(currentAnswer).some((key) => parseInt(currentAnswer[key], 10) < 0)
      ? false
      : answerIsValid;
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || data === null) return <div>Loading...</div>;
  if (error || !data) {
    const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
    captureError(new Error(`Error fetching useCounterpartsQuestionQuery: ${graphQLErrorsForAPM}`));
    return null;
  }

  const mentorships = data.participant.mentorships;
  const userIsMentor = data.participant.__typename === "Mentor";

  const label = customizations.prompt;

  return (
    <Question className="counterparts-info">
      <QuestionLabel label={label} description={description} questionIsOptional={optional} />
      {mentorships.map((mentorship) => {
        const counterpart = userIsMentor ? mentorship.student : mentorship.mentor;

        return (
          <Input
            className="counterpart"
            type={customizations.inputType}
            placeholder={placeholder ? placeholder : optional ? undefined : "Type here..."}
            min={customizations.inputType === "number" ? 0 : undefined}
            key={counterpart.id}
            label={`${counterpart.user.firstName} ${counterpart.user.lastName}`}
            value={(currentAnswer && currentAnswer[mentorship.id]) || ""}
            questionIsOptional={optional}
            onChange={({ currentTarget: { value } }) => {
              if (value.length < 1) {
                const updatedAnswer: Answer = { ...currentAnswer };
                delete updatedAnswer[mentorship.id];
                updateAnswer(updatedAnswer);
              } else {
                updateAnswer({ ...currentAnswer, [mentorship.id]: value });
              }
            }}
          />
        );
      })}
    </Question>
  );
}
